// React
import { Check } from "lucide-react";

// Hooks
import { useTranslation } from "@/shared/hooks";

/**
 * Props for VerificationStepper component
 */
interface VerificationStepperProps {
  currentStep: number;
  compact?: boolean;
}

/**
 * VerificationStepper Component
 *
 * Shows progress through the verification process (2 steps total)
 * Supports compact mode (circles only) for headers
 */
export function VerificationStepper({
  currentStep,
  compact = false,
}: VerificationStepperProps) {
  const t = useTranslation(); // Always call hook at top
  const steps = [1, 2];

  const stepLabels = [
    { number: 1, label: t("verificationStep1") },
    { number: 2, label: t("verificationStep2") },
  ];

  if (compact) {
    // Compact mode: circles only, no labels
    return (
      <div className="flex items-center gap-3">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step;
          const isCurrent = currentStep === step;
          const isLast = index === steps.length - 1;

          return (
            <div key={step} className="flex items-center">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs
                  transition-colors duration-200
                  ${
                    isCompleted
                      ? "bg-green-600 text-white"
                      : isCurrent
                        ? "bg-red-600 text-white"
                        : "bg-gray-300 text-gray-500"
                  }
                `}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : step}
              </div>
              {!isLast && <div className="w-8 h-0.5 mx-2 bg-gray-300" />}
            </div>
          );
        })}
      </div>
    );
  }

  // Full mode: circles with labels

  return (
    <div className="py-6 border-b border-gray-200 mb-6">
      <div className="flex items-center w-full">
        {stepLabels.map((step, index) => {
          const isCompleted = currentStep > step.number;
          const isCurrent = currentStep === step.number;
          const isLast = index === stepLabels.length - 1;

          return (
            <div key={step.number} className="flex items-center flex-1">
              {/* Step Circle and Label */}
              <div className="flex flex-col items-center w-full">
                <div
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm
                    transition-colors duration-200
                    ${
                      isCompleted
                        ? "bg-green-600 text-white"
                        : isCurrent
                          ? "bg-red-600 text-white"
                          : "bg-gray-200 text-gray-500"
                    }
                  `}
                >
                  {isCompleted ? <Check className="w-5 h-5" /> : step.number}
                </div>
                <span
                  className={`
                    mt-2 text-sm font-medium text-center
                    ${isCurrent ? "text-gray-900" : "text-gray-500"}
                  `}
                >
                  {step.label}
                </span>
              </div>

              {/* Connecting Line */}
              {!isLast && (
                <div>
                  <div
                    className={`
                      h-0.5 w-full transition-colors duration-200
                      ${isCompleted ? "bg-green-600" : "bg-gray-200"}
                    `}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
